/**
 * The build, as a list of steps.
 *
 * <h3>Why the sequence is data</h3>
 *
 * The approved surface is a spine of nodes: the platform's steps resolve
 * themselves, the person's steps stop it until answered, and the order on
 * screen is the order things happen. Written as branching code that is a tangle
 * nobody can check against the design. Written as a list it can be read beside
 * the mockup and compared line for line, which is the only way a surface like
 * this stays the one that was approved.
 *
 * <h3>What a step is</h3>
 *
 * {@code key}      how the answer is stored, and what the backend call reads
 * {@code ask}      the question as a person reads it, plus its hint and input
 * {@code node}     what the trail records once it is answered — a title, the
 *                  quieter line under it, and a tone when the answer means the
 *                  record will sit at something other than done
 *
 * Platform steps are not in here. They are recorded when the call that performs
 * them returns, never when the sequence reaches them: an ExteLet onboard is one
 * call, so claiming "identity issued" before it has run would be predicting.
 */

import {
  FUNCTION_MODE_OPTIONS, INDUSTRY_MODE_OPTIONS, OPERATION_MODE_OPTIONS,
  EXECUTION_MODE_OPTIONS,
} from './agentModes';
/* The Register stage's steps — order, labels and icons — are the platform's,
   owned by data/registerSteps.js and read by the registration wizard too, so
   the console and Protect cannot disagree about what Register is. */
import { registerStep } from './registerSteps';

/** Framework choices, labelled as a person would say them, valued as the backend knows them. */
export const FRAMEWORKS = [
  { label: 'Anthropic · Claude', platformType: 'ANTHROPIC' },
  { label: 'Microsoft · Copilot', platformType: 'MICROSOFT_COPILOT' },
  { label: 'OpenAI · ChatGPT', platformType: 'OPENAI' },
  { label: 'Google · Gemini', platformType: 'VERTEX_AI' },
  { label: 'LangChain · LangGraph', platformType: 'LANGGRAPH' },
];

const ownerNode = (intent) => (intent?.ownerMode === 'nominate'
  ? {
    title: 'Owner nominated',
    text: 'The record stays NOMINATED until they accept for themselves.',
    tone: 'hold',
  }
  : {
    title: 'You accepted accountability',
    text: 'Claimed and attested in one step — the record is ACTIVE.',
  });

/**
 * How calls reach the agent, in the platform's own two values.
 *
 * `integrationPath` is CODED or MCP on the onboard payload
 * (ExteletRegistrationWizard.jsx:333). The labels are the console's, so the two
 * surfaces describe the same choice the same way.
 */
/**
 * The console's Connect step, field for field.
 *
 * <h3>Why all six and not two</h3>
 *
 * The spine used to ask for a name and a vendor id. The other four were not
 * therefore unset: the onboard sent the backend's defaults and a literal
 * `1.0.0`, so an agent registered through Protect carried an invocation
 * contract nobody chose and nobody was shown. They are optional on the console
 * and optional here — pre-filled with the console's own values, and sent as
 * answered.
 *
 * <h3>proxiedOnly</h3>
 *
 * The vendor agent id and the invocation pattern describe an agent the gateway
 * dispatches to. A coded-SDK agent runs in the customer's own process and calls
 * the gateway itself; the backend sets its id to the minted Agent UUID. The
 * console hides both for those frameworks and so does this, which is why the
 * marker is on the field rather than written into a branch somewhere.
 */
export const INVOCATION_PATTERNS = [
  { value: 'SYNCHRONOUS', label: 'SYNCHRONOUS \u2014 request / response (recommended for LLM calls)' },
  { value: 'ASYNCHRONOUS', label: 'ASYNCHRONOUS \u2014 job submit + poll' },
  { value: 'STREAMING', label: 'STREAMING \u2014 server-sent events' },
  { value: 'WEBHOOK', label: 'WEBHOOK \u2014 fire-and-forget' },
];

export const CONNECT_FIELDS = [
  { key: 'name', label: 'Display name', placeholder: 'partner-llm-7' },
  { key: 'externalAgentVersion', label: 'Version', value: '1.0.0' },
  { key: 'externalAgentId', label: 'Its id in that framework', proxiedOnly: true,
    placeholder: 'partner-llm-7 or claude-3-5-sonnet-20241022' },
  { key: 'invocationPattern', label: 'Invocation pattern', value: 'SYNCHRONOUS',
    proxiedOnly: true, options: INVOCATION_PATTERNS },
  { key: 'maxTokensPerRequest', label: 'Max tokens / request', type: 'number', value: 8192 },
  { key: 'timeoutSeconds', label: 'Timeout (seconds)', type: 'number', value: 300 },
];

export const PATHS = [
  { value: 'CODED', label: 'Coded agent \u2014 you own the source' },
  { value: 'MCP', label: 'Off-the-shelf \u2014 route through the MCP gateway' },
];

export const integrationPathFor = (label) =>
  PATHS.find((p) => p.label === label)?.value || 'CODED';

/**
 * The ExteLet spine, step for step against the console's registration wizard.
 *
 * <h3>Where the commit is</h3>
 *
 * `commit: true` marks the last question before anything is created. The
 * console submits between its step 4 and step 5, and so does this: everything
 * above it is still a draft the person can change, and everything below it is
 * reporting what already happened. A step that claims work before the call
 * that performs it has returned is a prediction, not a record.
 *
 * <h3>Why steps carry a stage</h3>
 *
 * The journey strip says where the AGENT is. Until the onboard returns there is
 * no agent, so every question before the commit sits at the first stage however
 * many of them have been answered.
 */
export const EXTELET_STEPS = [
  {
    key: 'framework',
    stage: 'discovery',
    icon: registerStep('framework').icon,
    ask: {
      question: 'Which framework does it run on?',
      hint: 'Auth, health probes and routing come from the connection behind it.',
      input: 'choice',
      options: FRAMEWORKS.map((f) => f.label),
      confirm: 'Bind framework',
    },
    node: (v) => ({
      title: 'Framework chosen',
      text: `${v}. Nothing is registered yet.`,
    }),
  },
  {
    /* Whose connection, not where it answers. The console binds to a Platform
       Connector an admin configured and refuses to continue without one;
       Protect offers Prove7's, or one the customer adds through the same
       connector API, health-checked before anything is bound to it. */
    key: 'connection',
    stage: 'discovery',
    /* The console folds resolving the connector into binding the framework, so
       this shares that step's icon rather than being the one question in the
       stage with a round number beside it. */
    icon: registerStep('framework').icon,
    ask: {
      question: 'Whose connection should it run through?',
      hint: 'Auth, health probes and routing come from the connection \u2014 ours, or one you '
          + 'add here.',
      input: 'connection',
    },
    node: (v) => ({
      title: v?.provided ? 'Bound to a Prove7 connection' : 'Bound to your connection',
      text: `${v?.label || 'Connection'} \u2014 credentials stay on the connection and never `
          + 'travel with a call.',
    }),
  },
  {
    key: 'agent',
    stage: 'discovery',
    icon: registerStep('connect').icon,
    ask: {
      question: 'What is it called, and how should its calls run?',
      hint: 'The name is yours. Everything under it is the console\u2019s own Connect step, '
          + 'opened on the values it pre-fills \u2014 change them or leave them.',
      input: 'fields',
      fields: CONNECT_FIELDS,
      confirm: 'Use these',
    },
    node: (v) => ({
      title: 'Named',
      text: `${v?.name || 'This agent'}${v?.externalAgentId ? ` \u00b7 ${v.externalAgentId}` : ''}`,
    }),
  },
  {
    key: 'path',
    stage: 'discovery',
    icon: registerStep('path').icon,
    ask: {
      question: 'How should calls reach it?',
      hint: 'Anything that does not take this path is refused.',
      input: 'choice',
      options: PATHS.map((p) => p.label),
      confirm: 'Choose the path',
    },
    node: (v) => ({
      title: 'Path chosen',
      text: integrationPathFor(v) === 'MCP'
        ? 'It will point its calls at the gateway. Its own code does not change.'
        : 'The SDK wraps its entry point, so request and response are both on the record.',
    }),
  },
  {
    key: 'accountability',
    stage: 'discovery',
    icon: registerStep('accountability').icon,
    /* The last question before anything exists. */
    commit: true,
    ask: {
      question: 'Who answers for it?',
      hint: 'A named human. Accountability is accepted, not assigned \u2014 and nothing is '
          + 'registered until this is answered.',
      input: 'accountability',
      confirm: 'Register it',
    },
    node: ownerNode,
  },
  {
    key: 'credentials',
    stage: 'discovery',
    icon: registerStep('credentials').icon,
    ask: {
      question: 'Save its keys. They are shown once.',
      hint: 'Only hashes are kept \u2014 nothing can show you the full key again.',
      input: 'credentials',
    },
    node: (v) => ({
      title: 'Credentials minted',
      text: `${(v?.keys || []).join(' \u00b7 ') || 'Keys'} \u2014 scoped to this agent alone.`,
    }),
  },
  {
    key: 'wire',
    stage: 'discovery',
    icon: registerStep('wire').icon,
    ask: {
      question: 'Wire it up.',
      hint: 'The last thing that happens outside Prove7.',
      input: 'snippet',
    },
    node: (v) => ({
      title: 'Wiring taken',
      text: v?.language === 'mcp'
        ? 'Gateway configuration copied. Calls arrive protected from the first one.'
        : `${v?.language === 'python' ? 'Python' : 'Java'} snippet copied.`,
    }),
  },
  {
    /* The console's last registration step. It does not ask anything — it
       reports where the registration actually stands, which is the one thing
       Protect never said: the row is on file as PENDING_VERIFICATION and the
       first protected call verifies it with nothing to press. Somebody who is
       not told that has no reason to make the call that would verify it. */
    key: 'registered',
    stage: 'discovery',
    icon: registerStep('activate').icon,
    ask: {
      question: 'It is registered.',
      hint: 'Where its registration stands, and what moves it on.',
      input: 'registered',
    },
    node: (v) => (v?.registrationStatus === 'PENDING_VERIFICATION'
      ? { title: 'Registered \u00b7 awaiting its first call',
        text: 'On file and protected. The first protected call it makes verifies it.' }
      : { title: 'Registered',
        text: v?.registrationStatus
          ? `Its registration is ${v.registrationStatus}.`
          : 'On file and protected.' }),
  },
  {
    /* Journey stage 02, and the first step that is about the AGENT rather than
       about registering it. The platform assigned the name during the onboard;
       this shows it. Nothing is asked — identity is not a decision — but a
       stage whose definition is "cryptographic name stamped" has to put the
       name on screen rather than claim it in a sentence. */
    key: 'identity',
    stage: 'identity',
    ask: {
      question: 'The name it will answer to.',
      hint: 'Assigned at registration and carried on every call it makes. Nothing to decide '
          + '\u2014 worth seeing once.',
      input: 'identity',
    },
    node: (v) => (v?.spiffeId
      ? {
        title: 'Identity stamped',
        text: v.svidActive
          ? 'Its SPIFFE name, with a live certificate behind it.'
          : 'Its SPIFFE name. The certificate is issued the first time it presents itself.',
      }
      : { title: 'Identity not readable here',
        text: 'It is on the agent\u2019s own page in the full platform.', tone: 'hold' }),
  },
  {
    key: 'intent',
    stage: 'intent',
    ask: {
      question: 'What is it allowed to decide?',
      hint: 'Its Semantic Decision Layer. Attach one that is approved, describe one and send '
          + 'it for approval, or attach one later.',
      input: 'intent',
    },
    node: (v) => {
      if (v?.bound) {
        return { title: 'Decision layer attached',
          text: `${v.label} — its resolved skills are frozen on the binding.` };
      }
      if (v?.submitted) {
        return { title: 'Decision layer drafted · approval pending',
          text: `${v.label} is with an approver. Nothing is attached until they approve it.`,
          tone: 'hold' };
      }
      return { title: 'No decision layer yet',
        text: 'Attach one whenever it is ready. Until then the readiness gate reports it '
            + 'unmet.', tone: 'hold' };
    },
  },
  {
    /* Journey stage 04, and one of the readiness gate's nine checks. The gate
       counts grants that are not approval duties, so an approver alone leaves
       nobody able to run it — which is why this step says so rather than
       counting heads. */
    key: 'access',
    stage: 'access',
    ask: {
      question: 'Who may run it?',
      hint: 'At least one person or group needs real access. Naming an approver is a duty, '
          + 'not access, and the readiness gate does not count it.',
      input: 'access',
    },
    node: (v) => {
      if (v?.satisfied) {
        return { title: 'Access granted',
          text: `${v.granted} grant${v.granted === 1 ? '' : 's'} with runtime access. The `
              + 'readiness gate is met on access.' };
      }
      return { title: 'Nobody can run it yet',
        text: 'The readiness gate reports access unmet until somebody has view, execute, '
            + 'manage or admin on it.', tone: 'hold' };
    },
  },
  {
    /* Promote, and not the wizard's Register step 7 despite sharing its name.
       That step is a summary screen reporting PENDING_VERIFICATION; this one
       calls `gelMarkExecutionReady` (ActivateAsk.jsx:37), which is the
       Execution Ready promotion gate — journey stage 05. Two different actions
       wearing one word. */
    key: 'activate',
    stage: 'promote',
    ask: {
      question: 'Put it to work?',
      hint: 'Execution Ready is what admits its calls to protected traffic. The platform '
          + 'checks the same list it enforces, so anything unmet is shown before you ask.',
      input: 'activate',
    },
    node: (v) => (v?.activated
      ? { title: 'Execution Ready',
        text: 'Marked by the platform and sealed. Its calls are admitted to protected '
            + 'traffic from now on.' }
      : { title: 'Registered, not active', text: 'It is on the record and protected. Mark it '
          + 'execution ready whenever its gate is clear.', tone: 'hold' }),
  },
  {
    key: 'rules',
    stage: 'enforce',
    ask: {
      question: 'What must never leave?',
      hint: 'Each one is a rule your tenant already has. Switching it on applies it at the '
          + 'boundary, not in the model\u2019s instructions.',
      input: 'boundaries',
    },
    node: (v) => (v?.labels?.length
      ? {
        title: `${v.labels.length} boundary rule${v.labels.length === 1 ? '' : 's'} on`,
        text: `${v.labels.join(' \u00b7 ')} \u2014 enforced at the boundary.`,
      }
      : { title: 'No boundary rules on', text: 'Nothing is held back yet. They can be switched '
          + 'on at any time.', tone: 'hold' }),
  },
  {
    key: 'probe',
    stage: 'enforce',
    ask: {
      question: 'One live call, on the record.',
      hint: 'Nothing is proved by a probe it never ran.',
      input: 'choice',
      options: ['Run it now', 'Not yet'],
      confirm: 'Finish',
    },
    /* This step does not run the call — it takes you to where you make it. A
       node claiming "recorded and sealed" above a call nobody made would be
       the one lie this surface exists to prevent. */
    node: (v) => (v === 'Run it now'
      ? { title: 'Ready for its first call',
        text: 'Opening the Run Center, with the verdict shown under the answer.' }
      : { title: 'No call yet', text: 'Until one runs, nothing has been proved end to end.',
        tone: 'hold' }),
  },
];

/* ── an agent your team authored here ──────────────────────────────────── */

/**
 * The Let spine, against `AgentCreateSlideout` and the create it posts.
 *
 * <h3>What it asks, and what it leaves alone</h3>
 *
 * The form requires a name and types the agent on four dimensions; everything
 * else it offers — description, endpoint, gateway URL, API key — is optional
 * and belongs to an agent that already exists. So the spine asks for what the
 * create needs, and the rest is edited on the agent's own page afterwards.
 *
 * <h3>Why autonomous is not on the list</h3>
 *
 * `ck_autonomous_op_requires_full_trust` is a CHECK constraint, and a new
 * agent's trust_band defaults to SUPERVISED — trust is earned from governed
 * runs, not chosen at create time. Offering it here would mean collecting the
 * whole form and then failing on a constraint violation, which is the worst
 * moment to learn the rule.
 */
export const LET_STEPS = [
  {
    key: 'naming',
    stage: 'identity',
    ask: {
      question: 'What is it called, and what is it for?',
      hint: 'The name is how everyone will refer to it. The description is for the people '
          + 'who come after you.',
      input: 'fields',
      fields: [
        { key: 'name', label: 'Name', placeholder: 'IAM Sentry' },
        { key: 'description', label: 'What it is responsible for', placeholder: 'Optional' },
      ],
      confirm: 'Use these',
    },
    node: (v) => ({
      title: 'Named',
      text: v?.description ? `${v.name} \u2014 ${v.description}` : (v?.name || 'Named'),
    }),
  },
  {
    key: 'modes',
    stage: 'identity',
    ask: {
      question: 'What kind of agent is this?',
      hint: 'Four dimensions the platform types it on. Each one is checked at the database, '
          + 'so a value outside them is refused rather than stored.',
      input: 'choices',
      groups: [
        { key: 'functionMode', label: 'What it does', options: FUNCTION_MODE_OPTIONS },
        { key: 'industryMode', label: 'Where it works', options: INDUSTRY_MODE_OPTIONS },
        {
          key: 'operationMode',
          label: 'Who drives it',
          options: OPERATION_MODE_OPTIONS.filter((o) => o.value !== 'autonomous'),
          note: 'Autonomous needs Full trust, which is earned from protected runs. A new agent '
              + 'starts Supervised, so it cannot hold it yet.',
        },
        { key: 'executionMode', label: 'How it runs', options: EXECUTION_MODE_OPTIONS },
      ],
      confirm: 'Type it',
    },
    node: (v) => ({
      title: 'Typed',
      text: [v?.functionMode, v?.industryMode, v?.operationMode, v?.executionMode]
        .filter(Boolean).join(' \u00b7 '),
    }),
  },
  {
    key: 'accountability',
    stage: 'identity',
    commit: true,
    ask: {
      question: 'Who answers for it?',
      hint: 'A named human. Nothing is created until this is answered.',
      input: 'accountability',
    },
    node: ownerNode,
  },
  {
    key: 'rules',
    stage: 'enforce',
    ask: {
      question: 'What must never leave?',
      hint: 'Each one is a rule your tenant already has. Switching it on applies it at the '
          + 'boundary, not in the model\u2019s instructions.',
      input: 'boundaries',
    },
    node: (v) => (v?.labels?.length
      ? {
        title: `${v.labels.length} boundary rule${v.labels.length === 1 ? '' : 's'} on`,
        text: `${v.labels.join(' \u00b7 ')} \u2014 enforced at the boundary.`,
      }
      : { title: 'No boundary rules on', text: 'Nothing is held back yet. They can be switched '
          + 'on at any time.', tone: 'hold' }),
  },
  {
    key: 'probe',
    stage: 'enforce',
    ask: {
      question: 'One live call, on the record.',
      hint: 'Nothing is proved by a call nobody made.',
      input: 'choice',
      options: ['Run it now', 'Not yet'],
      confirm: 'Finish',
    },
    node: (v) => (v === 'Run it now'
      ? { title: 'Ready for its first call',
        text: 'Opening the Run Center, with the verdict shown under the answer.' }
      : { title: 'No call yet', text: 'Until one runs, nothing has been proved end to end.',
        tone: 'hold' }),
  },
];

/* ── an agent drafted from what you describe ───────────────────────────── */

/**
 * The AutoLet spine, against `AutoCreateLauncher` and the run it starts.
 *
 * <h3>Why it does not ask who answers for it</h3>
 *
 * Starting the pipeline creates no agent — it drafts one, and the agent is
 * created at review. Asking who is accountable for a thing that may never exist
 * puts the question in the wrong place; it is asked where the agent is real.
 *
 * <h3>Why the spine ends at the commit</h3>
 *
 * From the moment the run starts, its own event stream owns the trail. Two
 * producers writing the same feed interleave into nonsense.
 */
export const AUTOLET_STEPS = [
  {
    key: 'words',
    stage: 'identity',
    ask: {
      question: 'Describe what it should do.',
      hint: 'Whatever you would tell a new colleague on their first day. We draft it step by '
          + 'step and stop at anything we cannot work out.',
      input: 'fields',
      fields: [{ key: 'sourceRef', label: 'In your words' }],
      confirm: 'Use this',
    },
    node: (v) => ({
      title: 'Brief taken',
      text: v?.sourceRef ? `\u201c${String(v.sourceRef).slice(0, 90)}\u2026\u201d` : 'Brief taken',
    }),
  },
  {
    key: 'naming',
    stage: 'identity',
    ask: {
      question: 'What should it be called?',
      hint: 'A name you will recognise in a list of fifty.',
      input: 'fields',
      fields: [{ key: 'name', label: 'Name', placeholder: 'Renewals Assistant' }],
      confirm: 'Use this name',
    },
    node: (v) => ({ title: 'Named', text: v?.name || 'Named' }),
  },
  {
    key: 'modes',
    stage: 'identity',
    commit: true,
    ask: {
      question: 'What kind of agent should it draft?',
      hint: 'These are hints to the pipeline. Each is checked at the database, so a value '
          + 'outside them is refused rather than stored.',
      input: 'choices',
      groups: [
        { key: 'functionMode', label: 'What it does', options: FUNCTION_MODE_OPTIONS },
        { key: 'industryMode', label: 'Where it works', options: INDUSTRY_MODE_OPTIONS },
        {
          key: 'operationMode',
          label: 'Who drives it',
          options: OPERATION_MODE_OPTIONS.filter((o) => o.value !== 'autonomous'),
          note: 'Autonomous needs Full trust, which is earned from protected runs. A drafted '
              + 'agent starts Supervised, so it cannot hold it yet.',
        },
        {
          key: 'executionMode',
          label: 'How it runs',
          /* `auto` is the pipeline choosing from the source, which is the
             launcher's own default and the reason this list differs from the
             native one. */
          options: [
            { value: 'auto', label: 'auto \u2014 let the pipeline choose from the source' },
            ...EXECUTION_MODE_OPTIONS,
          ],
          note: null,
        },
      ],
      confirm: 'Start drafting',
    },
    node: () => ({
      title: 'Drafting started',
      text: 'Every step it takes appears below as it happens.',
    }),
  },
];

/** The spine for an archetype, or nothing when we do not have one for it. */
export function scriptFor(archetype) {
  const key = String(archetype || '').toLowerCase();
  if (key === 'extelet') return EXTELET_STEPS;
  if (key === 'let') return LET_STEPS;
  if (key === 'autolet') return AUTOLET_STEPS;
  /* No guessing. Walking somebody down the registration spine for a thing that
     is not registered is worse than having no spine for it. */
  return null;
}

export default EXTELET_STEPS;
